// Toggles between the fractal (default boot) and the M4/M5 cathedral on
// hotkey C. Manages scene membership so only one mode's root is mounted
// at a time — keeps update cost predictable and avoids double-rendering.
export class ModeSwitcher {
  constructor({ fractal, cathedral, parentScene, onStateChange, onModeChange }) {
    this.fractal = fractal;
    this.cathedral = cathedral;
    this.parentScene = parentScene;
    this.onStateChange = typeof onStateChange === 'function' ? onStateChange : null;
    this.onModeChange = typeof onModeChange === 'function' ? onModeChange : null;
    this.current = 'fractal';
    parentScene.add(fractal.root);
    this._onKey = this._onKey.bind(this);
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this._onKey);
    }
  }

  _addCathedral() {
    this.parentScene.add(this.cathedral.root);
    // Cathedral exposes scene-level lights as siblings of its root.
    if (this.cathedral.ambientLight) this.parentScene.add(this.cathedral.ambientLight);
    if (this.cathedral.keyLight) this.parentScene.add(this.cathedral.keyLight);
  }

  _removeCathedral() {
    this.parentScene.remove(this.cathedral.root);
    if (this.cathedral.ambientLight) this.parentScene.remove(this.cathedral.ambientLight);
    if (this.cathedral.keyLight) this.parentScene.remove(this.cathedral.keyLight);
  }

  getCurrent() {
    return this.current;
  }

  toggle() {
    // PR-C4: C now toggles an IN-SHADER mirror-cathedral environment on
    // the fractal itself, rather than swapping out the whole fractal
    // scene for the legacy wireframe cathedral. Stays in 'fractal' mode
    // for hotkey purposes; only the BG renders differently.
    if (typeof this.fractal?.toggleCathedral === 'function') {
      const on = this.fractal.toggleCathedral();
      this._showToast(`cathedral: ${on ? 'on' : 'off'}`);
      if (this.onModeChange) this.onModeChange(on ? 'cathedral' : 'fractal');
      return;
    }
    // Legacy scene-swap fallback (older fractal builds without the
    // cathedral uniform). Kept so older bundles still toggle something.
    if (this.current === 'fractal') {
      this.parentScene.remove(this.fractal.root);
      this._addCathedral();
      this.current = 'cathedral';
    } else {
      this._removeCathedral();
      this.parentScene.add(this.fractal.root);
      this.current = 'fractal';
    }
    if (this.onModeChange) this.onModeChange(this.current);
  }

  update(signals, t, frameMs) {
    const active = this.current === 'fractal' ? this.fractal : this.cathedral;
    if (active?.update) active.update(signals, t, frameMs);
  }

  _onKey(e) {
    if (e.key === 'c' || e.key === 'C') {
      this.toggle();
      return;
    }
    if ((e.key === 'f' || e.key === 'F') && this.current === 'fractal') {
      const name = this.fractal?.cycleFractal?.();
      if (name) this._showToast(`fractal: ${name}`);
      this._emitStateChange();
      return;
    }
    if ((e.key === 'p' || e.key === 'P') && this.current === 'fractal') {
      const name = this.fractal?.cyclePalette?.();
      if (name) this._showToast(`palette: ${name}`);
      this._emitStateChange();
      return;
    }
    if ((e.key === 'v' || e.key === 'V') && this.current === 'fractal') {
      const mode = this.fractal?.cycleCameraMode?.();
      if (mode) {
        const label = (mode === 'fly') ? 'fly-through'
          : (mode === 'stabilized') ? 'stabilized' : 'orbit';
        this._showToast(`camera: ${label}`);
      }
      this._emitStateChange();
    }
  }

  _emitStateChange() {
    if (!this.onStateChange || !this.fractal) return;
    this.onStateChange({
      palette: this.fractal._paletteId ?? 0,
      fractal: this.fractal.currentFractalId ?? 0,
      view: this.fractal.cameraMode ?? 'fly',
    });
  }

  _showToast(text) {
    if (typeof document === 'undefined') return;
    if (!this._toast) {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;top:24px;left:50%;transform:translateX(-50%);'
        + 'padding:8px 18px;background:rgba(0,0,0,0.55);color:#fff;font:600 14px/1 system-ui,sans-serif;'
        + 'letter-spacing:0.08em;text-transform:uppercase;border-radius:6px;pointer-events:none;'
        + 'opacity:0;transition:opacity 100ms ease-out;z-index:9999;';
      document.body?.appendChild(el);
      this._toast = el;
    }
    this._toast.textContent = text;
    this._toast.style.opacity = '1';
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { this._toast.style.opacity = '0'; }, 1500);
  }

  dispose() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this._onKey);
    }
    clearTimeout(this._toastTimer);
    if (this._toast?.parentNode) this._toast.parentNode.removeChild(this._toast);
    this.fractal?.dispose?.();
    this.cathedral?.dispose?.();
  }
}
